import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  Loader2,
  RefreshCw,
  X,
  Trash2,
  UserPlus,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const inputClass =
  "w-full bg-[#040812]/70 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3 text-sm rounded-sm transition-colors";

const emptyVendor = {
  email: "",
  name: "",
  password: "",
  vendor_type: "external",
};

export default function AdminVendors({ onVendorsChanged }) {
  const { authHeader, apiBase } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyVendor });

  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/vendors`, {
        headers: authHeader(),
      });
      setVendors(data);
      onVendorsChanged?.(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load vendors",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader, onVendorsChanged]);

  useEffect(() => {
    load();
  }, [load]);

  const createVendor = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || form.password.length < 8) {
      toast.error("Fill all fields (password min 8 chars).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${apiBase}/admin/vendors`, form, {
        headers: authHeader(),
      });
      setVendors((v) => [data, ...v]);
      onVendorsChanged?.([data, ...vendors]);
      toast.success("Vendor created.");
      setForm({ ...emptyVendor });
      setShowForm(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Create failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      const { data } = await axios.patch(
        `${apiBase}/admin/vendors/${v.id}`,
        { active: !(v.active !== false) },
        { headers: authHeader() },
      );
      setVendors((list) => list.map((x) => (x.id === v.id ? data : x)));
      toast.success(data.active ? "Activated." : "Deactivated.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Update failed",
      );
    }
  };

  const resetPassword = async (v) => {
    const pw = window.prompt(`Set a new password for ${v.name}:`);
    if (!pw) return;
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    try {
      await axios.patch(
        `${apiBase}/admin/vendors/${v.id}`,
        { password: pw },
        { headers: authHeader() },
      );
      toast.success("Password updated. Share it with the vendor privately.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Update failed",
      );
    }
  };

  const deleteVendor = async (v) => {
    if (
      !window.confirm(
        `Delete vendor "${v.name}"? Their leads will become unassigned.`,
      )
    )
      return;
    try {
      await axios.delete(`${apiBase}/admin/vendors/${v.id}`, {
        headers: authHeader(),
      });
      setVendors((list) => list.filter((x) => x.id !== v.id));
      toast.success("Vendor deleted.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed",
      );
    }
  };

  const verifyToggle = async (v, field) => {
    const current = v[field]?.verified;
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/vendors/${v.id}/verify`,
        { [field]: !current },
        { headers: authHeader() },
      );
      setVendors((list) =>
        list.map((x) => (x.id === v.id ? { ...x, [field]: data[field] } : x)),
      );
      toast.success(!current ? `${field.toUpperCase()} verified.` : `${field.toUpperCase()} unverified.`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Update failed",
      );
    }
  };

  const totalLeads = useMemo(
    () => vendors.reduce((s, v) => s + (v.lead_count || 0), 0),
    [vendors],
  );

  return (
    <div data-testid="admin-vendors-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">/Admin · Vendors</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Partner vendors
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Internal reps and external partners. They see only leads assigned
            to them.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-vendors-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid="admin-vendors-new"
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
          >
            <UserPlus size={14} />
            Invite vendor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-[#0B132B]/50 border border-white/10 p-6">
          <div className="font-display text-white text-3xl">{vendors.length}</div>
          <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
            Total vendors
          </div>
        </div>
        <div className="bg-[#0B132B]/50 border border-white/10 p-6">
          <div className="font-display text-white text-3xl">
            {vendors.filter((v) => v.active !== false).length}
          </div>
          <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
            Active
          </div>
        </div>
        <div className="bg-[#0B132B]/50 border border-white/10 p-6">
          <div className="font-display text-white text-3xl">{totalLeads}</div>
          <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
            Assigned leads
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Name", "Email", "Type", "Leads", "Created", "Status", "Actions"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <Loader2 size={20} className="inline animate-spin text-slate-500" />
                  </td>
                </tr>
              )}
              {!loading && vendors.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-slate-500 text-sm">
                    No vendors yet. Click <b>Invite vendor</b> to create one.
                  </td>
                </tr>
              )}
              {!loading &&
                vendors.map((v) => (
                  <tr
                    key={v.id}
                    data-testid={`admin-vendor-row-${v.id}`}
                    className="border-b border-white/5 last:border-0"
                  >
                    <td className="px-6 py-4 text-white">{v.name}</td>
                    <td className="px-6 py-4 text-slate-300">{v.email}</td>
                    <td className="px-6 py-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {v.vendor_type || "external"}
                    </td>
                    <td className="px-6 py-4 text-white">{v.lead_count || 0}</td>
                    <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                      {v.created_at
                        ? new Date(v.created_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${
                          v.active !== false
                            ? "bg-green-500/15 text-green-300 border-green-500/40"
                            : "bg-slate-500/15 text-slate-400 border-slate-500/40"
                        }`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {v.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 justify-end flex-wrap">
                        <button
                          onClick={() => verifyToggle(v, "kyc")}
                          data-testid={`admin-vendor-verify-kyc-${v.id}`}
                          className={`text-xs px-2 py-1 rounded-sm border transition-colors ${
                            v.kyc?.verified
                              ? "bg-green-500/15 text-green-300 border-green-500/40"
                              : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                          }`}
                          title={v.kyc?.verified ? "KYC verified — click to unverify" : "Mark KYC verified"}
                        >
                          KYC {v.kyc?.verified ? "✓" : "○"}
                        </button>
                        <button
                          onClick={() => verifyToggle(v, "bank")}
                          data-testid={`admin-vendor-verify-bank-${v.id}`}
                          className={`text-xs px-2 py-1 rounded-sm border transition-colors ${
                            v.bank?.verified
                              ? "bg-green-500/15 text-green-300 border-green-500/40"
                              : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                          }`}
                          title={v.bank?.verified ? "Bank verified — click to unverify" : "Mark bank verified"}
                        >
                          Bank {v.bank?.verified ? "✓" : "○"}
                        </button>
                        <button
                          onClick={() => toggleActive(v)}
                          className="text-slate-400 hover:text-white text-xs inline-flex items-center gap-1"
                          title={v.active !== false ? "Deactivate" : "Activate"}
                        >
                          {v.active !== false ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        </button>
                        <button
                          onClick={() => resetPassword(v)}
                          className="text-slate-400 hover:text-white text-xs"
                        >
                          Reset pw
                        </button>
                        <button
                          onClick={() => deleteVendor(v)}
                          className="text-slate-500 hover:text-red-500 text-xs inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New vendor modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />
          <form
            onSubmit={createVendor}
            className="relative w-full max-w-md bg-[#0B132B] border border-white/10 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline">Invite vendor</div>
                <h2 className="font-display text-white text-2xl tracking-tight mt-2">
                  New vendor account
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="overline mb-1.5 block">Name *</label>
                <input
                  data-testid="vendor-form-name"
                  value={form.name}
                  onChange={set("name")}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="overline mb-1.5 block">Email *</label>
                <input
                  data-testid="vendor-form-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="overline mb-1.5 block">
                  Temporary password (min 8) *
                </label>
                <input
                  data-testid="vendor-form-password"
                  type="text"
                  value={form.password}
                  onChange={set("password")}
                  className={inputClass}
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="overline mb-1.5 block">Vendor type</label>
                <select
                  value={form.vendor_type}
                  onChange={set("vendor_type")}
                  className={inputClass}
                >
                  <option value="external">External partner</option>
                  <option value="internal">Internal rep</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="vendor-form-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white text-sm font-medium px-6 py-3 rounded-sm transition-colors"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Create vendor
              </button>
              <p className="text-xs text-slate-500">
                Share the login URL <span className="text-slate-300">/crm/login</span> and this password with the vendor. They can request a reset from admin at any time.
              </p>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
